import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer, TimeoutError } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';

/**
 * Timeout + retry dla żądań GET/HEAD.
 *
 * Powód: iOS (WebKit — także Chrome na iPhone) zamraża sockety przy
 * backgroundowaniu karty / zmianie sieci Wi-Fi↔LTE. Requesty na takim
 * na-wpół-martwym połączeniu HTTP/2 wiszą, aż stos sieciowy systemu je
 * porzuci (zwykle ~15 s, bywa dłużej). Główny zysk tego interceptora to
 * AUTO-RETRY: po natywnym błędzie sieci (status 0) lub własnym timeoutcie
 * ponawiamy strzał, który idzie już świeżym połączeniem — user nie musi
 * klikać ponownie.
 *
 * TIMEOUT_MS to efektywnie CAŁKOWITY budżet czasu na request: dla zwykłego
 * GET-a JSON (bez reportProgress) HttpClient emituje tylko Sent i Response,
 * więc `each` nie ma zdarzeń postępu do resetu i działa jak total-timeout od
 * wysłania. Dlatego próg jest hojny (20 s), by NIE ubić wolnej-ale-żywej
 * dużej listy na słabym LTE — łapie tylko zamrożony socket (który nie emituje
 * niczego) i patologiczne zawisy dłuższe niż natywny timeout.
 *
 * Tylko metody idempotentne (GET/HEAD) — ponowienie mutacji groziłoby
 * np. podwójną rezerwacją. Pomijamy też odpowiedzi inne niż JSON
 * (blob/arraybuffer/text — pobieranie/eksport może legalnie trwać długo).
 */
const TIMEOUT_MS = 20000;
const RETRY_DELAY_MS = 1000;

@Injectable()
export class ApiTimeoutInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isIdempotent = request.method === 'GET' || request.method === 'HEAD';
    if (!isIdempotent || request.responseType !== 'json') {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      // `each` zamiast `first`: HttpClient emituje event Sent od razu po
      // wysłaniu, więc `first` nigdy by nie zadziałał na czekanie na response.
      timeout({ each: TIMEOUT_MS }),
      retry({
        count: 1,
        delay: (error) => {
          const isTimeout = error instanceof TimeoutError;
          const isNetworkError = error instanceof HttpErrorResponse && error.status === 0;
          if (isTimeout || isNetworkError) {
            return timer(RETRY_DELAY_MS);
          }
          // Błędy HTTP (4xx/5xx) — nie ponawiamy, przekaż dalej
          return throwError(() => error);
        },
      }),
      catchError((error) => {
        // Zamień TimeoutError na HttpErrorResponse, żeby HttpErrorInterceptor
        // i handlery komponentów dostały znajomy kształt błędu (status 0).
        if (error instanceof TimeoutError) {
          return throwError(() => new HttpErrorResponse({
            status: 0,
            statusText: 'Timeout',
            url: request.url,
          }));
        }
        return throwError(() => error);
      }),
    );
  }
}
